import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiGet, apiPut } from '../services/api';

// Asks an organization admin, once, how facility service requests should work. Their answer is saved to
// the organization's service expansion policy and can be changed later in Settings - Service Access.
const CHOICES = [
  {
    key: 'approve_all',
    label: 'Facilities can request; I approve every request',
    policy: { facilityCanRequest: true, requiresOrgApproval: true },
  },
  {
    key: 'approve_org_paid',
    label: 'Facilities can request; I approve only when the organization pays',
    policy: { facilityCanRequest: true, requiresOrgApproval: false },
  },
  {
    key: 'no_requests',
    label: 'Facilities cannot request; only I request service changes',
    policy: { facilityCanRequest: false, requiresOrgApproval: true },
  },
];

export default function FacilityRequestPolicyPrompt() {
  const [organization, setOrganization] = useState(null);
  const [saving, setSaving] = useState('');

  useEffect(() => {
    let active = true;
    apiGet('/organizations/me')
      .then((res) => { if (active) setOrganization(res?.organization || null); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!organization || organization.serviceExpansionPolicy?.decidedAt) return null;

  const choose = async (choice) => {
    setSaving(choice.key);
    try {
      const res = await apiPut(`/organizations/${organization._id}`, { serviceExpansionPolicy: choice.policy });
      setOrganization((prev) => ({ ...prev, serviceExpansionPolicy: res?.organization?.serviceExpansionPolicy || { ...choice.policy, decidedAt: new Date().toISOString() } }));
      toast.success('Saved. You can change this any time in Settings.');
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.response?.data?.message || 'Could not save your choice');
    } finally {
      setSaving('');
    }
  };

  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50 p-5" aria-labelledby="facility-request-policy-title">
      <h2 id="facility-request-policy-title" className="text-base font-semibold text-amber-900">
        Decide how your facilities request service changes
      </h2>
      <p className="mt-1 text-sm text-amber-800">
        Facility admins can ask Stratel to add or turn off a service for their own facility. Requests the organization
        would pay for always need your approval. Choose whether you also approve requests a facility pays for itself.
        You are told whenever a facility asks to turn off a service the organization added.
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {CHOICES.map((choice) => (
          <button
            key={choice.key}
            type="button"
            disabled={Boolean(saving)}
            onClick={() => choose(choice)}
            className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-left text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
          >
            {saving === choice.key ? 'Saving...' : choice.label}
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-amber-700">
        You can change this later in{' '}
        <Link to="/settings?tab=organization&orgTab=internal-access-policy" className="font-medium underline">
          Settings - Internal Access Policy
        </Link>.
      </p>
    </section>
  );
}
